import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle, Plug } from 'lucide-react';

import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useAccountingIntegrationsStatus,
  useSetDefaultAccountingIntegration,
} from '@/hooks/use-integrations';
import { useQuickBooksStatus } from '@/hooks/use-payroll';
import { useXeroStatus, useXeroConnect, useXeroDisconnect } from '@/hooks/use-integrations';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

/**
 * Settings > Integrations page.
 *
 * Minimal v1 for S13: shows QuickBooks + Xero cards, lets the admin
 * connect/disconnect Xero, and if both are connected shows a warning +
 * a "pick default" toggle.
 */
export function IntegrationsSettingsPage() {
  const orgTimeZone = useOrgTimeZone();
  const { data: summary, isLoading: summaryLoading } =
    useAccountingIntegrationsStatus();
  const { data: qb } = useQuickBooksStatus();
  const { data: xero, isLoading: xeroLoading } = useXeroStatus();

  const connectXero = useXeroConnect();
  const disconnectXero = useXeroDisconnect();
  const setDefault = useSetDefaultAccountingIntegration();
  const xeroEnabled = summary?.xero_enabled !== false;

  const handleConnectXero = async () => {
    try {
      const result = await connectXero.mutateAsync();
      window.location.href = result.authorization_url;
    } catch {
      // handled by mutation
    }
  };

  if (summaryLoading || xeroLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/settings">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Integrations
          </h1>
          <p className="text-gray-600 mt-1">
            Connect external accounting systems to sync invoices and payments.
          </p>
        </div>
      </div>

      {xeroEnabled && summary?.warn_both_connected && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="font-semibold text-amber-900">
                    Both QuickBooks and Xero are connected
                  </h3>
                  <p className="text-sm text-amber-800 mt-1">
                    H3ight will push new invoices to the default integration.
                    Pick which one should be the default for this org.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={
                      summary.default_integration === 'quickbooks'
                        ? 'default'
                        : 'outline'
                    }
                    size="sm"
                    onClick={() => setDefault.mutate('quickbooks')}
                    disabled={setDefault.isPending}
                  >
                    Default: QuickBooks
                  </Button>
                  <Button
                    variant={
                      summary.default_integration === 'xero'
                        ? 'default'
                        : 'outline'
                    }
                    size="sm"
                    onClick={() => setDefault.mutate('xero')}
                    disabled={setDefault.isPending}
                  >
                    Default: Xero
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* QuickBooks card — read-only summary that links to the existing page. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {qb?.connected ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <Plug className="h-5 w-5 text-gray-400" />
            )}
            QuickBooks Online
          </CardTitle>
          <CardDescription>
            Push expenses, invoices and payroll to QuickBooks Online.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={qb?.connected ? 'default' : 'secondary'}>
              {qb?.connected ? 'Connected' : 'Not connected'}
            </Badge>
            {qb?.connected && summary?.default_integration === 'quickbooks' && (
              <Badge variant="outline">Default</Badge>
            )}
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/payroll/quickbooks">Manage</Link>
          </Button>
        </CardContent>
      </Card>

      {/* Xero card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {xero?.connected ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <Plug className="h-5 w-5 text-gray-400" />
            )}
            Xero
          </CardTitle>
          <CardDescription>
            {xeroEnabled
              ? 'Push sales invoices and pull payments from Xero.'
              : 'Xero is disabled for this platform. Nothing is sent to or pulled from Xero.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={xero?.connected ? 'default' : 'secondary'}>
              {xero?.connected ? 'Connected' : 'Not connected'}
            </Badge>
            {!xeroEnabled && <Badge variant="secondary">Xero is disabled</Badge>}
            {xero?.connected && summary?.default_integration === 'xero' && (
              <Badge variant="outline">Default</Badge>
            )}
            {xero?.is_token_expired && (
              <Badge variant="destructive">Token expired</Badge>
            )}
          </div>
          {xero?.connected && (
            <div className="text-sm text-gray-600 space-y-1">
              {xero.tenant_name && (
                <div>
                  <span className="text-gray-500">Tenant:</span>{' '}
                  <span className="font-medium">{xero.tenant_name}</span>
                </div>
              )}
              {xero.tenant_id && (
                <div>
                  <span className="text-gray-500">Tenant ID:</span>{' '}
                  <span className="font-mono text-xs">{xero.tenant_id}</span>
                </div>
              )}
              {xero.last_synced && (
                <div>
                  <span className="text-gray-500">Last synced:</span>{' '}
                  {formatDateTimeInTimeZone(xero.last_synced, orgTimeZone)}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2">
            {xero?.connected ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => disconnectXero.mutate()}
                disabled={disconnectXero.isPending}
              >
                {disconnectXero.isPending ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            ) : xeroEnabled && (
              <Button
                size="sm"
                onClick={handleConnectXero}
                disabled={connectXero.isPending}
              >
                <Plug className="h-4 w-4 mr-2" />
                {connectXero.isPending ? 'Connecting...' : 'Connect to Xero'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
